import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Modal from '../Modal/Modal';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';

export default function EarnPointsModal({ show, onClose, card, onSuccess }) {
    const { settings } = useAuth();
    const earnPoints = Number(settings['loyalty_earn_points'] ?? 10);
    const earnPer = Number(settings['loyalty_earn_per'] ?? 100);

    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const amountNum = parseFloat(amount) || 0;
    const pointsToEarn = Math.floor(amountNum / earnPer) * earnPoints;
    const isValid = amountNum >= earnPer;

    const handleSubmit = async () => {
        if (!isValid) return;
        setLoading(true);
        try {
            await axios.post(
                `${API_BASE_URL}/api/v1/loyalty-cards/${card.id}/earn`,
                {
                    orderAmount: amountNum,
                    description: description || undefined,
                },
                { withCredentials: true }
            );
            toast.success(`+${pointsToEarn} points crédités sur la carte`);
            setAmount('');
            setDescription('');
            onSuccess?.();
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors du crédit de points');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setAmount('');
        setDescription('');
        onClose?.();
    };

    return (
        <Modal
            show={show}
            onClose={handleClose}
            title="Créditer des points"
            icon={faStar}
            iconBg="#fef3c7"
            iconColor="#d97706"
            footer={
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleClose}
                        style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!isValid || loading}
                        style={{
                            padding: '8px 18px', borderRadius: 8, border: 'none',
                            background: isValid ? '#d97706' : '#e5e7eb',
                            color: isValid ? '#fff' : '#9ca3af',
                            cursor: isValid ? 'pointer' : 'not-allowed',
                            fontSize: 13, fontWeight: 600,
                        }}
                    >
                        {loading ? 'Traitement...' : 'Créditer'}
                    </button>
                </div>
            }
        >
            <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Rate reminder */}
                <div style={{ background: '#fef3c7', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#92400e' }}>
                    <strong>Règle :</strong> {earnPoints} point{earnPoints > 1 ? 's' : ''} pour chaque tranche de {earnPer} dhs d'achat
                </div>

                {/* Amount input */}
                <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                        Montant de l'achat (dhs)
                    </label>
                    <input
                        type="number"
                        value={amount}
                        min={100}
                        step={10}
                        onChange={e => setAmount(e.target.value)}
                        placeholder={`Ex: ${earnPer * 2}`}
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                    {amountNum > 0 && amountNum < earnPer && (
                        <p style={{ fontSize: 12, color: '#d97706', margin: '4px 0 0' }}>
                            Minimum {earnPer} dhs pour gagner des points
                        </p>
                    )}
                </div>

                {/* Description */}
                <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                        Description (optionnel)
                    </label>
                    <input
                        type="text"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Ex: Achat ordonnance n°1234"
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14,
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </div>

                {/* Points preview */}
                {pointsToEarn > 0 && (
                    <div style={{
                        background: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        borderRadius: 10,
                        padding: '14px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <div>
                            <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Points à créditer</div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                {Math.floor(amountNum / earnPer)} × {earnPoints} pts
                            </div>
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#059669' }}>
                            +{pointsToEarn} pts
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
